import { ClearRounded } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
// Direct module import, not the controls barrel: this file is exported FROM the
// barrel, so importing it back would make a cycle.
import Button from "./Button";
import { useFormatPrice } from "../hooks";
import { sellableUnitOptions, unitPriceFor } from "../utils/cartLine";

/**
 * Which unit is being sold, asked at the moment a product is added.
 *
 * Without it a screen can only ever add the base unit, so building a mixed-unit
 * sale means adding a line and then correcting it — twice the taps at a busy
 * counter. Shared by POS (both templates) and the back-office sales grids, so a
 * product offers the same units wherever it is added.
 *
 * Props:
 * - prod: the product (needs baseUnitId, baseUnit, productUnits)
 * - basePrice: the product's own price, before any unit conversion
 * - filterFor: "sale" | "purchase" | undefined — passed to sellableUnitOptions
 * - handleAddUnit: (prod, option, close) => void
 * - setShowForm: (bool) => void
 */
const UnitPickerDialog = (props) => {
  const { t } = useTranslation();
  const formatPrice = useFormatPrice();
  const { setShowForm, prod, basePrice, handleAddUnit, filterFor = "sale" } = props;
  const options = sellableUnitOptions(prod, filterFor);

  const handleClose = () => setShowForm(false);

  return (
    <div className="p-[15px] grid gap-[15px]">
      {/* Header */}
      <div className="mb-[5px] flex justify-between items-center">
        <span className="text-[16px] text-gray-500">{t("selectUnit")}</span>
        <div>
          <IconButton onClick={handleClose}>
            <ClearRounded />
          </IconButton>
        </div>
      </div>

      {/* Body */}
      <div className="grid gap-[15px]">
        <Grid container spacing={2}>
          <Grid item xs={12} className="pb-[10px]">
            <span className="gray-color text-[20px]">
              {t("selectUnitFor")} "
              <span className="font-medium">{prod?.name}</span> "
            </span>
          </Grid>

          {options.map((opt) => (
            <Grid item xs={12} md={options.length === 2 ? 6 : 4} key={opt.id}>
              <Button
                onClick={() => handleAddUnit(prod, opt, handleClose)}
                className="p-[10px] w-[100%] h-[60px] !text-[18px] !font-normal"
                size="large"
                variant="text"
                sx={{ minWidth: "180px" }}
              >
                <span className="grid text-center leading-tight">
                  <span>{opt.label}</span>
                  <span className="text-[13px] text-gray-500">
                    {formatPrice(unitPriceFor(opt, basePrice))}
                  </span>
                </span>
              </Button>
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );
};

export default UnitPickerDialog;
